<template>
  <div class="moa-page pa-10">

    <!-- ── Header ── -->
    <div class="d-flex flex-wrap justify-space-between align-center mb-7" style="gap: 16px;">
      <div>
        <div class="page-title">MOAs & Agreements</div>
        <div class="page-sub mt-1">Manage institutional partnerships and legal documents</div>
      </div>
      <div class="d-flex align-center" style="gap: 10px;">
        <v-text-field
          v-model="search"
          placeholder="Search partner or document..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="width: 240px;"
          class="search-field"
        />
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          item-title="label"
          item-value="value"
          variant="outlined"
          density="compact"
          hide-details
          style="width: 160px;"
          class="filter-select"
        />
      </div>
    </div>

    <!-- ── Table ── -->
    <v-card class="table-card rounded-xl" elevation="0">
      <v-data-table
        :headers="headers"
        :items="filteredData"
        :loading="loading"
        hover
        class="moa-table"
      >
        <!-- Custom header -->
        <template #headers="{ columns }">
          <tr>
            <th
              v-for="col in columns"
              :key="col.key"
              class="table-th"
              :style="col.align === 'center' ? 'text-align: center' : ''"
            >
              {{ col.title }}
            </th>
          </tr>
        </template>

        <!-- Document -->
        <template #[`item.document`]="{ item }">
          <div class="d-flex align-center" style="gap: 12px;">
            <div class="doc-icon-box rounded-lg d-flex align-center justify-center flex-shrink-0">
              <v-icon color="#3b82f6" size="20">mdi-file-document-outline</v-icon>
            </div>
            <div>
              <div class="doc-type">{{ docTypeLabel(item.agreement?.agreement_type) }}</div>
            </div>
          </div>
        </template>

        <!-- Partner Organization -->
        <template #[`item.name`]="{ item }">
          <span class="org-name">{{ item.name }}</span>
        </template>

        <!-- Upload Date -->
        <template #[`item.uploaded_date`]="{ item }">
          <span class="date-text">{{ formatDateWorded(item.agreement?.createdAt) }}</span>
        </template>

        <!-- Valid Until -->
        <template #[`item.valid_until`]="{ item }">
          <div class="d-flex align-center" style="gap: 6px; flex-wrap: wrap;">
            <span class="date-text">{{ formatDateWorded(item.agreement?.validity_date) }}</span>
            <span
              v-if="item.agreement?.validity_date && isExpired(item.agreement.validity_date)"
              class="expired-label"
            >(Expired)</span>
            <v-tooltip
              v-else-if="item.agreement?.validity_date && isExpiringSoon(item.agreement.validity_date)"
              :text="`Expires in ${daysUntil(item.agreement.validity_date)} day${daysUntil(item.agreement.validity_date) !== 1 ? 's' : ''}`"
              location="top"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props" size="15" color="#f59e0b">mdi-alert-outline</v-icon>
              </template>
            </v-tooltip>
          </div>
        </template>

        <!-- Status -->
        <template #[`item.status`]="{ item }">
          <div class="d-flex justify-center">
            <div class="status-chip d-flex align-center" :style="statusStyle(getMoaStatus(item.agreement))">
              <v-icon size="14" :color="statusIconColor(getMoaStatus(item.agreement))" class="mr-1">
                {{ statusIcon(getMoaStatus(item.agreement)) }}
              </v-icon>
              {{ statusLabel(getMoaStatus(item.agreement)) }}
            </div>
          </div>
        </template>

        <!-- Actions -->
        <template #[`item.actions`]="{ item }">
          <div class="d-flex justify-center">
            <v-btn
              icon
              variant="text"
              size="small"
              color="#94a3b8"
              :disabled="!item.agreement?.filename"
              @click="openViewDialog(item)"
            >
              <v-icon size="18">mdi-eye-outline</v-icon>
            </v-btn>
          </div>
        </template>

        <template #no-data>
          <div class="text-center text-grey py-8">No MOAs or agreements found.</div>
        </template>
      </v-data-table>
    </v-card>

    <!-- ── View Dialog ── -->
    <v-dialog v-model="viewDialog" max-width="1400" persistent>
      <v-card rounded="lg" elevation="4" style="overflow: hidden;">
        <div class="d-flex" style="height: 85vh; min-height: 520px;">

          <!-- Left: PDF Viewer (dark) -->
          <div class="pdf-panel d-flex flex-column">
            <div v-if="pdfLoading" class="d-flex flex-column align-center justify-center flex-grow-1" style="gap: 12px;">
              <v-progress-circular indeterminate color="#94a3b8" size="36" />
              <div class="pdf-loading-text">Loading document...</div>
            </div>
            <div v-else-if="!selectedItem?.agreement?.filename" class="d-flex flex-column align-center justify-center flex-grow-1" style="gap: 10px;">
              <v-icon size="48" color="#475569">mdi-file-document-off-outline</v-icon>
              <div class="pdf-empty-text">No document uploaded</div>
            </div>
            <iframe
              v-else-if="pdfUrl"
              :src="pdfUrl"
              class="pdf-iframe"
              frameborder="0"
            />
            <div v-else class="d-flex flex-column align-center justify-center flex-grow-1" style="gap: 10px;">
              <v-icon size="40" color="#64748b">mdi-alert-circle-outline</v-icon>
              <div class="pdf-empty-text">Failed to load document</div>
            </div>
          </div>

          <!-- Right: Details -->
          <div class="details-panel pa-6 d-flex flex-column overflow-y-auto">

            <!-- Panel header -->
            <div class="d-flex justify-space-between align-start mb-5">
              <div class="details-title">Details</div>
              <v-btn icon="mdi-close" variant="text" size="small" color="#9ca3af" @click="closeViewDialog" />
            </div>

            <!-- Detail rows -->
            <div class="detail-grid">

              <div class="detail-row">
                <div class="detail-label">Partner Organization</div>
                <div class="detail-value">{{ selectedItem?.name ?? '—' }}</div>
              </div>

              <div class="detail-row">
                <div class="detail-label">Document Type</div>
                <div class="detail-value">{{ docTypeLabel(selectedItem?.agreement?.agreement_type) }}</div>
              </div>

              <div class="detail-row">
                <div class="detail-label">Document ID</div>
                <div class="detail-value">#{{ selectedItem?.agreement?.id ?? '—' }}</div>
              </div>

              <div class="detail-row">
                <div class="detail-label">Upload Date</div>
                <div class="detail-value">{{ formatDateWorded(selectedItem?.agreement?.createdAt) }}</div>
              </div>

              <div class="detail-row">
                <div class="detail-label">Valid Until</div>
                <div class="d-flex align-center" style="gap: 6px; flex-wrap: wrap;">
                  <div class="detail-value">{{ formatDateWorded(selectedItem?.agreement?.validity_date) }}</div>
                  <span
                    v-if="selectedItem?.agreement?.validity_date && isExpired(selectedItem.agreement.validity_date)"
                    class="expired-label"
                  >(Expired)</span>
                  <span
                    v-else-if="selectedItem?.agreement?.validity_date && isExpiringSoon(selectedItem.agreement.validity_date)"
                    class="expiring-soon-label"
                  >Expires soon</span>
                </div>
              </div>

              <div class="detail-row">
                <div class="detail-label">Status</div>
                <div class="mt-1">
                  <div
                    class="status-chip d-flex align-center"
                    :style="statusStyle(getMoaStatus(selectedItem?.agreement))"
                  >
                    <v-icon size="13" :color="statusIconColor(getMoaStatus(selectedItem?.agreement))" class="mr-1">
                      {{ statusIcon(getMoaStatus(selectedItem?.agreement)) }}
                    </v-icon>
                    {{ statusLabel(getMoaStatus(selectedItem?.agreement)) }}
                  </div>
                </div>
              </div>

            </div>

          </div>
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
export default {
  name: "MoaAndAgreementDataTable",

  data: () => ({
    search: "",
    statusFilter: "all",
    statusOptions: [
      { label: "All Statuses", value: "all" },
      { label: "Active", value: "active" },
      { label: "Expiring Soon", value: "expiring" },
      { label: "Expired", value: "expired" },
      { label: "Pending Review", value: "pending" },
    ],
    headers: [
      { title: "Document",             value: "document",      align: "start",  sortable: false },
      { title: "Partner Organization", value: "name",          align: "start",  sortable: true  },
      { title: "Upload Date",          value: "uploaded_date", align: "start",  sortable: false },
      { title: "Valid Until",          value: "valid_until",   align: "start",  sortable: false },
      { title: "Status",               value: "status",        align: "center", sortable: false },
      { title: "Actions",              value: "actions",       align: "center", sortable: false },
    ],
    tabledata: [],
    loading: false,
    viewDialog: false,
    selectedItem: null,
    pdfUrl: null,
    pdfLoading: false,
    fadeAwayMessage: {
      show: false, type: "success", header: "System Message", message: "", top: 10,
    },
  }),

  computed: {
    filteredData() {
      let result = this.tabledata;
      if (this.statusFilter !== "all") {
        result = result.filter(
          (item) => this.getMoaStatus(item.agreement) === this.statusFilter
        );
      }
      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter(
          (item) =>
            (item.name || "").toLowerCase().includes(q) ||
            (item.agreement?.agreement_type || "").toLowerCase().includes(q)
        );
      }
      return result;
    },
  },

  mounted() {
    this.initialize();
  },

  methods: {
    initialize() {
      this.loading = true;
      this.axiosCall(
        "/organization-program-agreement/loadOrganizationTiedByInstitute/" +
          this.$store.state.user.user.instituteID,
        "GET"
      ).then((res) => {
        if (res) this.tabledata = res.data;
        this.loading = false;
      });
    },

    getMoaStatus(agreement) {
      if (!agreement || !agreement.validity_date) return "pending";
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const validity = new Date(agreement.validity_date);
      const sixtyDays = new Date(today);
      sixtyDays.setDate(sixtyDays.getDate() + 60);
      if (validity < today) return "expired";
      if (validity <= sixtyDays) return "expiring";
      return "active";
    },

    isExpired(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },

    statusLabel(status) {
      const map = {
        active: "Active",
        expiring: "Renewal Needed",
        expired: "Expired",
        pending: "Pending Review",
      };
      return map[status] || "Unknown";
    },

    statusStyle(status) {
      const map = {
        active:   { background: "#dcfce7", color: "#16a34a" },
        expiring: { background: "#fef3c7", color: "#d97706" },
        expired:  { background: "#fee2e2", color: "#dc2626" },
        pending:  { background: "#fef9c3", color: "#b45309" },
      };
      return map[status] || { background: "#f1f5f9", color: "#374151" };
    },

    statusIcon(status) {
      const map = {
        active:   "mdi-check-circle-outline",
        expiring: "mdi-alert-circle-outline",
        expired:  "mdi-alert-circle-outline",
        pending:  "mdi-clock-outline",
      };
      return map[status] || "mdi-help-circle-outline";
    },

    statusIconColor(status) {
      return this.statusStyle(status).color;
    },

    docTypeLabel(type) {
      const map = {
        moa: "MOA",
        mou: "MOU",
        partnership_agreement: "Partnership Agreement",
      };
      return map[type] || (type ? type.toUpperCase() : "Document");
    },

    formatDateWorded(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d)) return "—";
      return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    },

    isExpiringSoon(dateStr) {
      if (!dateStr) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const validity = new Date(dateStr);
      const fifteenDays = new Date(today);
      fifteenDays.setDate(fifteenDays.getDate() + 15);
      return validity >= today && validity <= fifteenDays;
    },

    daysUntil(dateStr) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const validity = new Date(dateStr);
      validity.setHours(0, 0, 0, 0);
      return Math.ceil((validity - today) / (1000 * 60 * 60 * 24));
    },

    async openViewDialog(item) {
      this.selectedItem = item;
      this.viewDialog = true;
      this.pdfUrl = null;
      if (!item.agreement?.filename) return;
      this.pdfLoading = true;
      const url =
        process.env.VUE_APP_SERVER +
        "/organization-program-agreement/getFile/" +
        item.agreement.filename;
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const blob = await response.blob();
        this.pdfUrl = window.URL.createObjectURL(blob);
      } catch (e) {
        console.error("Failed to load PDF", e);
      } finally {
        this.pdfLoading = false;
      }
    },

    closeViewDialog() {
      this.viewDialog = false;
      if (this.pdfUrl) {
        window.URL.revokeObjectURL(this.pdfUrl);
        this.pdfUrl = null;
      }
      this.selectedItem = null;
    },

  },
};
</script>

<style scoped>
.moa-page {
  min-height: 100vh;
}

/* ── Header ── */
.page-title {
  font-size: 30px;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.5px;
  line-height: 1.2;
}
.page-sub {
  font-size: 14px;
  color: #6b7280;
}

/* ── Search / Filter ── */
.search-field :deep(.v-field) {
  border-radius: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
}
.search-field :deep(.v-field__outline__start),
.search-field :deep(.v-field__outline__notch),
.search-field :deep(.v-field__outline__end) {
  border-color: #e2e8f0 !important;
}
.filter-select :deep(.v-field) {
  border-radius: 10px;
  background: #fff;
}
.filter-select :deep(.v-field__outline__start),
.filter-select :deep(.v-field__outline__notch),
.filter-select :deep(.v-field__outline__end) {
  border-color: #e2e8f0 !important;
}

/* ── Table Card ── */
.table-card {
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

/* ── Custom Table Header ── */
.table-th {
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em !important;
  text-transform: uppercase !important;
  color: #6b7280 !important;
  background: #ffffff !important;
  border-bottom: 1px solid #f3f4f6 !important;
  padding: 14px 16px !important;
  white-space: nowrap;
}

/* ── Row ── */
.moa-table :deep(tbody tr:hover td) { background: #f9fafb; }
.moa-table :deep(tbody td) {
  font-size: 13.5px;
  border-bottom: 1px solid #f3f4f6 !important;
  padding: 16px !important;
}

/* ── Document cell ── */
.doc-icon-box {
  width: 38px;
  height: 38px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  flex-shrink: 0;
}
.doc-type {
  font-size: 13.5px;
  font-weight: 700;
  color: #111827;
}
.doc-id {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 1px;
}

/* ── Org name ── */
.org-name {
  font-size: 13.5px;
  font-weight: 500;
  color: #374151;
}

/* ── Dates ── */
.date-text {
  font-size: 13.5px;
  color: #374151;
}
.expired-label {
  font-size: 12px;
  font-weight: 600;
  color: #dc2626;
}
.expiring-soon-label {
  font-size: 12px;
  font-weight: 600;
  color: #d97706;
}

/* ── Status chip ── */
.status-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 99px;
  font-size: 12.5px;
  font-weight: 600;
  white-space: nowrap;
}

/* ── PDF Panel ── */
.pdf-panel {
  flex: 1 1 0;
  background: #1e2433;
  display: flex;
  flex-direction: column;
}
.pdf-iframe {
  width: 100%;
  flex: 1;
  border: none;
  display: block;
}
.pdf-loading-text, .pdf-empty-text {
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
}

/* ── Details Panel ── */
.details-panel {
  flex: 0 0 300px;
  min-width: 280px;
  background: #ffffff;
  border-left: 1px solid #e5e7eb;
}

.details-title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
}

/* ── Detail rows ── */
.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.detail-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;
}
.detail-row:last-child { border-bottom: none; }
.detail-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
}
.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}
.filename-text {
  font-size: 12.5px;
  font-family: monospace;
  color: #374151;
  word-break: break-all;
}
</style>
